import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

interface UsePaymentRecoveryPollingOptions {
  enabled: boolean;
  onSuccess: () => void;
  pollInterval?: number;
  maxDuration?: number;
}

/**
 * Auto-recovery polling hook for Apple Pay/Google Pay payments.
 * When enabled, polls subscription status every few seconds to catch
 * cases where the payment succeeded in Stripe but the callback failed.
 */
export function usePaymentRecoveryPolling({
  enabled,
  onSuccess,
  pollInterval = 2000,
  maxDuration = 30000,
}: UsePaymentRecoveryPollingOptions) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const successCalledRef = useRef(false);

  const stopPolling = useCallback(() => {
    // Only log if we're actually stopping something
    if (pollingRef.current || timeoutRef.current) {
      console.log('[PaymentRecoveryPolling] Stopping polling');
    }
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const checkSubscriptionStatus = useCallback(async () => {
    if (successCalledRef.current) return;
    
    try {
      console.log('[PaymentRecoveryPolling] Checking subscription status...');
      const { data, error } = await supabase.functions.invoke('check-subscription-status');
      
      if (error) {
        console.error('[PaymentRecoveryPolling] Error checking status:', error);
        return;
      }
      
      console.log('[PaymentRecoveryPolling] Status:', data);
      
      if (data?.subscribed || data?.subscription_status === 'active') {
        console.log('[PaymentRecoveryPolling] *** SUBSCRIPTION DETECTED VIA POLLING ***');
        successCalledRef.current = true;
        stopPolling();
        
        // Invalidate queries to ensure UI updates
        queryClient.invalidateQueries({ queryKey: ['subscriptionStatus'] });
        
        // Call success callback
        try {
          onSuccess();
        } catch (e) {
          console.error('[PaymentRecoveryPolling] onSuccess callback error:', e);
          // Force navigate as ultimate fallback
          window.location.href = '/?payment_success=true';
        }
      }
    } catch (e) {
      console.error('[PaymentRecoveryPolling] Poll error:', e);
    }
  }, [onSuccess, queryClient, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) {
      console.log('[PaymentRecoveryPolling] Already polling');
      return;
    }
    
    successCalledRef.current = false;
    console.log('[PaymentRecoveryPolling] Starting polling, interval:', pollInterval, 'max:', maxDuration);
    
    // Immediate first check
    checkSubscriptionStatus();
    
    // Start interval
    pollingRef.current = setInterval(checkSubscriptionStatus, pollInterval);
    
    // Auto-stop after maxDuration
    timeoutRef.current = setTimeout(() => {
      console.log('[PaymentRecoveryPolling] Max duration reached, stopping');
      stopPolling();
    }, maxDuration);
  }, [pollInterval, maxDuration, checkSubscriptionStatus, stopPolling]);

  // Start/stop based on enabled prop
  useEffect(() => {
    if (enabled) {
      startPolling();
    } else {
      stopPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [enabled, startPolling, stopPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling: !!pollingRef.current,
  };
}
